import { Constants } from '../../internal/Constants';
import Styles from './Label.module.css';
import { KeyboardEvent, Ref, useCallback, useMemo } from 'react';

/** Props for the Label component. */
interface ILabelDTO {
  text: string;
  title?: string; // Tooltip text; defaults to the label's own text. Pass an empty string to suppress the tooltip so a container-level tooltip can win.
  rtl?: boolean;
  classNameContainer?: string;
  ref?: Ref<HTMLLabelElement | null>;
  onClickHandler?: () => void;
}

/**
 * @param props - Component props.
 * @returns A styled Label element.
 */
export function Label(props: ILabelDTO) {
  const { text, title, rtl, ref, classNameContainer, onClickHandler } = props;

  // Handle key down to mimic click
  const handleKeyDown = useCallback((handler: () => void, e: KeyboardEvent) => {
    if (e.key === Constants.KeyMimicClick) {
      e.preventDefault();
      handler();
    }
  }, []);

  // Interactive props
  const interactiveProps = useMemo(() => {
    if (!onClickHandler) return {};
    const onClick = onClickHandler;
    const onKeyDown = handleKeyDown.bind(null, onClickHandler);
    return {
      tabIndex: 0,
      onClick,
      onKeyDown
    };
  }, [onClickHandler, handleKeyDown]);

  // Compute class names
  const containerClassName = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);

  // Prefix title with RLM for correct bidi rendering in tooltips. An explicit empty string suppresses the automatic tooltip (no title attribute is rendered), so a container-level tooltip on an
  // ancestor wins instead of the label repeating its own text.
  const resolvedTitle = useMemo(() => {
    if (title === '') return undefined;
    const raw = title ?? text;
    return rtl && raw ? `\u200F${raw}` : raw;
  }, [title, text, rtl]);

  // Render Label element
  const dir = useMemo(() => (rtl ? 'rtl' : undefined), [rtl]);
  return (
    <label ref={ref} className={containerClassName} dir={dir} title={resolvedTitle} {...interactiveProps}>
      {text}
    </label>
  );
}
